import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

export default function PasswordResetPage() {
  const { supabase } = useAuth();
  const [status, setStatus] = useState('processing'); // 'processing', 'ready', 'success', 'error'
  const [message, setMessage] = useState('Processing password reset...');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        // Get the URL query parameters (new approach)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenHash = urlParams.get('token_hash');
        const type = urlParams.get('type');
        const redirectTo = urlParams.get('redirect_to');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('[PasswordReset] Processing reset:', { 
          hasTokenHash: !!tokenHash, 
          type,
          redirectTo,
          error, 
          errorDescription 
        });

        // Check for errors first
        if (error) {
          console.error('[PasswordReset] Reset error:', error, errorDescription);
          setStatus('error');
          setMessage('Password reset link is invalid or has expired. Please request a new password reset.');
          return;
        }

        // If we have token hash and it's a recovery type, verify and show password form
        if (tokenHash && type === 'recovery') {
          console.log('[PasswordReset] Verifying password reset token...');
          
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery'
          });

          if (verifyError) {
            console.error('[PasswordReset] Error verifying token:', verifyError);
            setStatus('error');
            setMessage('Failed to verify password reset link. The link may have expired or is invalid.');
            return;
          }

          if (data.user) {
            console.log('[PasswordReset] Token verified successfully, showing password form');
            setStatus('ready');
            setMessage('Please enter your new password below.');
          } else {
            setStatus('error');
            setMessage('Authentication failed. Please request a new password reset.');
          }
        } else {
          // No token hash or wrong type
          console.warn('[PasswordReset] Invalid reset link');
          setStatus('error');
          setMessage('Invalid password reset link. Please request a new password reset.');
        }
      } catch (err) {
        console.error('[PasswordReset] Unexpected error during password reset:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please request a new password reset.');
      }
    };

    handlePasswordReset();
  }, [supabase]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        console.error('[PasswordReset] Error updating password:', updateError);
        setError(updateError.message);
        setLoading(false);
        return;
      }

      console.log('[PasswordReset] Password updated successfully');
      setStatus('success');
      setMessage('Password updated successfully!');
      setLoading(false);

    } catch (err) {
      console.error('[PasswordReset] Unexpected error updating password:', err);
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  const handleGoToDashboard = () => {
    // User is already logged in after successful password reset
    navigate('/', { replace: true });
  };

  const handleRequestNewReset = () => {
    navigate('/forgot-password', { replace: true });
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <div 
            className="spinner" 
            style={{ 
              width: 48, 
              height: 48, 
              border: '4px solid #eee', 
              borderTop: '4px solid #20b2aa', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }} 
          />
        );
      case 'ready':
        return (
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto' }}
          >
            <circle cx="12" cy="12" r="10" fill="#20b2aa"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4M5 11h14l-1 7H6l-1-7Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'success':
        return (
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto' }}
          >
            <circle cx="12" cy="12" r="10" fill="#20b2aa"/>
            <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'error':
        return (
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto' }}
          >
            <circle cx="12" cy="12" r="10" fill="#e74c3c"/>
            <path d="m15 9-6 6m0-6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#20b2aa';
      case 'error':
        return '#e74c3c';
      case 'ready':
        return '#20b2aa';
      default:
        return '#555';
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', padding: 40, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'center' }}>
        <img 
          src="https://amicredit.ro/wp-content/uploads/2024/02/amicredit.svg" 
          alt="AMI Credit" 
          style={{ width: 180, margin: '0 auto 12px auto' }} 
          onError={(e) => {
            e.target.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.innerHTML = '<span style="color: #20b2aa; font-weight: 700; font-size: 24px;">Ami</span><span style="color: #374151; font-weight: 700; font-size: 24px;">Credit</span>';
            fallback.style.textAlign = 'center';
            fallback.style.margin = '0 auto 12px auto';
            e.target.parentNode.insertBefore(fallback, e.target);
          }}
        />
        
        {getStatusIcon()}
        
        <h2 style={{ margin: 0, fontWeight: 700, color: getStatusColor() }}>
          {status === 'processing' && 'Processing Reset'}
          {status === 'ready' && 'Set New Password'}
          {status === 'success' && 'Password Updated!'}
          {status === 'error' && 'Reset Failed'}
        </h2>
        
        <p style={{ margin: 0, color: '#555', lineHeight: 1.5 }}>
          {message}
        </p>
        
        {status === 'ready' && (
          <form onSubmit={handlePasswordUpdate} style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontWeight: 500 }}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                style={{ padding: 10, borderRadius: 6, border: '1.5px solid #eee', outline: 'none', fontSize: 16 }}
                required
                minLength={6}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontWeight: 500 }}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                style={{ padding: 10, borderRadius: 6, border: '1.5px solid #eee', outline: 'none', fontSize: 16 }}
                required
                minLength={6}
              />
            </div>
            
            {error && (
              <div style={{ color: '#e74c3c', fontSize: 14, textAlign: 'center' }}>
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={loading}
              style={{ 
                background: '#20b2aa', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                padding: '12px 0', 
                fontSize: 17, 
                fontWeight: 600, 
                cursor: 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        )}
        
        {status === 'success' && (
          <button 
            onClick={handleGoToDashboard}
            style={{ 
              background: '#20b2aa', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '12px 0', 
              fontSize: 17, 
              fontWeight: 600, 
              cursor: 'pointer',
              marginTop: 12
            }}
          >
            Go to Dashboard
          </button>
        )}
        
        {status === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button 
              onClick={handleRequestNewReset}
              style={{ 
                background: '#e65c00', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                padding: '12px 0', 
                fontSize: 17, 
                fontWeight: 600, 
                cursor: 'pointer'
              }}
            >
              Request New Reset
            </button>
            <button 
              onClick={handleGoToLogin}
              style={{ 
                background: 'transparent', 
                color: '#555', 
                border: '1px solid #ddd', 
                borderRadius: 6, 
                padding: '12px 0', 
                fontSize: 15, 
                fontWeight: 500, 
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
